import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { KeyRound, Lock, AlertCircle } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Tx } from "../context/AutoTranslateContext";

const ResetPassword = () => {
  const { resetPassword } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) setError("Missing reset token. Please request a new reset link.");
  }, [token]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    const r = await resetPassword(token, password);
    setBusy(false);
    if (r.ok) nav("/"); else setError(r.error);
  };

  return (
    <div data-testid="reset-page" className="max-w-md mx-auto px-4 py-16">
      <div className="bg-white rounded-3xl border border-gold/25 p-8 shadow-sm">
        <div className="text-center mb-6">
          <div className="inline-flex w-12 h-12 rounded-full bg-forest/10 text-forest items-center justify-center mb-3">
            <KeyRound className="w-5 h-5" />
          </div>
          <h1 className="font-heading text-3xl text-forest font-bold mb-2"><Tx>Choose a new password</Tx></h1>
          <p className="text-sm text-mute"><Tx>Enter a new password to complete the reset.</Tx></p>
        </div>

        <form onSubmit={onSubmit} className="space-y-4">
          <label className="block">
            <span className="text-xs uppercase tracking-widest text-mute"><Tx>New password (min 8 characters)</Tx></span>
            <div className="mt-1.5 relative">
              <Lock className="w-4 h-4 text-mute absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                data-testid="reset-password" type="password" autoComplete="new-password" required minLength={8}
                value={password} onChange={(e) => setPassword(e.target.value)}
                className="w-full pl-10 pr-4 py-3 rounded-full bg-cream border border-forest/10 text-ink outline-none focus:border-forest transition"
              />
            </div>
          </label>
          {error && (
            <div data-testid="reset-error" className="flex items-start gap-2 text-sm text-clay bg-clay/10 rounded-2xl p-3">
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}
          <button
            data-testid="reset-submit"
            type="submit" disabled={busy || !token}
            className="w-full py-3 rounded-full bg-forest text-sand hover:bg-sage transition font-medium disabled:opacity-60"
          >
            {busy ? <Tx>Updating…</Tx> : <Tx>Update password</Tx>}
          </button>
        </form>

        <p className="mt-5 text-center text-xs text-mute">
          <Link data-testid="link-back-login" to="/login" className="text-forest hover:underline"><Tx>Back to sign in</Tx></Link>
        </p>
      </div>
    </div>
  );
};

export default ResetPassword;
